// Field Prototype — zone-based spatial knowledge environment
// Four Cognitive Zones organize your work by the kind of attention it needs:
//   Focused Execution (top-left)  — what you're actively working on
//   Reactive Triage (top-right) — things demanding attention
//   Reflective Thinking (bottom-left) — strategy, analysis, ideas
//   Collaborative / Waiting (bottom-right) — in others' hands
//
// A unit can appear in MULTIPLE zones, showing different facet information
// per zone. Morrison in "Focused Execution" shows "Active negotiation",
// while Morrison in "Reactive Triage" shows "3 gaps detected".

import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import UnitPresence, { UnitData, UnitType, PresenceShape, CognitiveZone, ChildPresence } from './GlowingPresence';
import FieldToolbar from './FieldToolbar';
import DetailPanel from './DetailPanel';
import Walkthrough from './Walkthrough';

// ============================================================
// Page header
// ============================================================

const MODES = [
  { id: 'field', label: 'Field', icon: '◈' },
  { id: 'graph', label: 'Graph', icon: '◉' },
  { id: 'timeline', label: 'Timeline', icon: '▬' },
  { id: 'list', label: 'List', icon: '☰' },
] as const;

function PageHeader({ onSearch }: { onSearch: () => void }) {
  const [activeMode, setActiveMode] = React.useState('field');

  return (
    <div style={{
      height: 48,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 20px',
      background: '#FFFFFF',
      borderBottom: '1px solid #E3E5EB',
      flexShrink: 0,
      fontFamily: '-apple-system, system-ui, sans-serif',
      zIndex: 100,
    }}>
      {/* Left: Logo + product name */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 26,
          height: 26,
          borderRadius: 6,
          background: 'linear-gradient(135deg, #2563EB, #7C3AED)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          color: '#FFFFFF',
          fontWeight: 800,
        }}>
          ◈
        </div>
        <span style={{ fontSize: 15, fontWeight: 700, color: '#111318', letterSpacing: -0.3 }}>
          Field
        </span>
        <span style={{ fontSize: 11, fontWeight: 500, color: '#A3A8B4', marginLeft: -4 }}>
          by Prismatic
        </span>
      </div>

      {/* Center: Mode switcher */}
      <div data-walkthrough="mode-switcher" style={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        background: '#F6F7F9',
        borderRadius: 8,
        padding: 3,
      }}>
        {MODES.map(mode => {
          const active = mode.id === activeMode;
          return (
            <button
              key={mode.id}
              onClick={() => setActiveMode(mode.id)}
              style={{
                border: 'none',
                background: active ? '#FFFFFF' : 'transparent',
                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                borderRadius: 6,
                padding: '4px 12px',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: active ? 700 : 500,
                color: active ? '#111318' : '#7A7F8E',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                fontFamily: 'inherit',
                transition: 'all 0.15s ease',
              }}
            >
              <span style={{ fontSize: 11 }}>{mode.icon}</span>
              {mode.label}
            </button>
          );
        })}
      </div>

      {/* Right: Search + notifications + avatar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          data-walkthrough="header-search"
          onClick={onSearch}
          style={{
            border: '1px solid #E3E5EB',
            background: '#F6F7F9',
            borderRadius: 8,
            padding: '5px 12px',
            cursor: 'pointer',
            fontSize: 11,
            color: '#7A7F8E',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: 'inherit',
            fontWeight: 500,
          }}
        >
          <span style={{ fontSize: 12 }}>🔍</span>
          Search
          <span style={{
            fontSize: 9,
            color: '#A3A8B4',
            background: '#FFFFFF',
            border: '1px solid #E3E5EB',
            borderRadius: 4,
            padding: '1px 5px',
            fontWeight: 600,
            marginLeft: 4,
          }}>
            ⌘K
          </span>
        </button>

        <button style={{
          border: '1px solid #E3E5EB',
          background: '#F6F7F9',
          borderRadius: 8,
          width: 32,
          height: 32,
          cursor: 'pointer',
          fontSize: 14,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}>
          🔔
          <div style={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: '#DC2626',
            border: '1.5px solid #FFFFFF',
          }} />
        </button>

        <div style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #2563EB, #0D9488)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 700,
          color: '#FFFFFF',
          cursor: 'pointer',
        }}>
          KC
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Layout constants
// ============================================================

const CAMERA_Z = 8;
const DISTANCE_FACTOR = 8;
const FOV = 50;
const CARD_PADDING_WU = 0.35;

// Zone regions in world coordinates — tuned for camera z=8, fov=50
// Each zone is a quadrant with center (cx, cy) and size (w, h)
const ZONE_REGIONS: Record<CognitiveZone, { cx: number; cy: number; w: number; h: number }> = {
  [CognitiveZone.FOCUSED_EXECUTION]:     { cx: -3.2, cy: 2.0, w: 5.5, h: 3.2 },
  [CognitiveZone.REACTIVE_TRIAGE]:       { cx: 3.2,  cy: 2.0, w: 5.5, h: 3.2 },
  [CognitiveZone.REFLECTIVE_THINKING]:   { cx: -3.2, cy: -2.0, w: 5.5, h: 3.2 },
  [CognitiveZone.COLLABORATIVE_WAITING]: { cx: 3.2,  cy: -2.0, w: 5.5, h: 3.2 },
};

const ZONE_LABELS: Record<CognitiveZone, { label: string; subtitle: string }> = {
  [CognitiveZone.FOCUSED_EXECUTION]:     { label: 'Working On', subtitle: 'Active execution' },
  [CognitiveZone.REFLECTIVE_THINKING]:   { label: 'Thinking About', subtitle: 'Strategy & analysis' },
  [CognitiveZone.REACTIVE_TRIAGE]:       { label: 'Needs Attention', subtitle: 'Demands your action' },
  [CognitiveZone.COLLABORATIVE_WAITING]: { label: 'Waiting On', subtitle: 'In others\' hands' },
};

// Zone accent colors — very subtle tints for background
const ZONE_COLORS: Record<CognitiveZone, { bg: string; accent: string; labelColor: string }> = {
  [CognitiveZone.FOCUSED_EXECUTION]:     { bg: '#4DC4D2', accent: '#4DC4D2', labelColor: 'rgba(77, 196, 210, 0.35)' },
  [CognitiveZone.REACTIVE_TRIAGE]:       { bg: '#E94257', accent: '#E94257', labelColor: 'rgba(233, 66, 87, 0.35)' },
  [CognitiveZone.REFLECTIVE_THINKING]:   { bg: '#4056A6', accent: '#4056A6', labelColor: 'rgba(64, 86, 166, 0.35)' },
  [CognitiveZone.COLLABORATIVE_WAITING]: { bg: '#892987', accent: '#892987', labelColor: 'rgba(137, 41, 135, 0.35)' },
};

// ============================================================
// Layout solver — positions & scales cards to fill their zone
// ============================================================

/** Convert a CSS card width to world units at a given scale */
function cardWorldWidth(heat: number, scale: number): number {
  const cssPx = (220 + heat * 120) * scale;
  const renderedPx = cssPx * (DISTANCE_FACTOR / CAMERA_Z);
  const visibleWidthWU = 2 * Math.tan((FOV / 2) * Math.PI / 180) * CAMERA_Z * 1.6;
  const pxPerWU = 1440 / visibleWidthWU;
  return renderedPx / pxPerWU;
}

function cardWorldHeight(heat: number, scale: number): number {
  return cardWorldWidth(heat, scale) * 0.65;
}

/**
 * Run the position solver at a given scale.
 * Returns final positions and whether any overlaps remain.
 */
function runSolver(
  heats: number[],
  scale: number,
  zone: { cx: number; cy: number; w: number; h: number }
): { pos: [number, number][]; hasOverlap: boolean } {
  const n = heats.length;

  // Seed positions: spread within zone
  const pos: [number, number][] = heats.map((heat, i) => {
    const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
    const maxR = Math.min(zone.w, zone.h) * 0.3;
    const r = 0.15 + (1 - heat) * (maxR - 0.15);
    return [
      zone.cx + Math.cos(angle) * r,
      zone.cy + Math.sin(angle) * r,
    ];
  });

  const halfW = heats.map(h => cardWorldWidth(h, scale) / 2 + CARD_PADDING_WU);
  const halfH = heats.map(h => cardWorldHeight(h, scale) / 2 + CARD_PADDING_WU);

  for (let iter = 0; iter < 120; iter++) {
    // Push apart overlapping cards
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const dx = pos[j][0] - pos[i][0];
        const dy = pos[j][1] - pos[i][1];
        const minSepX = halfW[i] + halfW[j];
        const minSepY = halfH[i] + halfH[j];
        const overlapX = minSepX - Math.abs(dx);
        const overlapY = minSepY - Math.abs(dy);

        if (overlapX > 0 && overlapY > 0) {
          if (overlapX < overlapY) {
            const push = overlapX / 2 + 0.01;
            const sign = dx >= 0 ? 1 : -1;
            pos[i][0] -= sign * push;
            pos[j][0] += sign * push;
          } else {
            const push = overlapY / 2 + 0.01;
            const sign = dy >= 0 ? 1 : -1;
            pos[i][1] -= sign * push;
            pos[j][1] += sign * push;
          }
        }
      }
    }

    // Gentle pull toward zone center
    for (let i = 0; i < n; i++) {
      pos[i][0] += (zone.cx - pos[i][0]) * 0.02;
      pos[i][1] += (zone.cy - pos[i][1]) * 0.02;
    }

    // Clamp inside zone boundaries
    for (let i = 0; i < n; i++) {
      const minX = zone.cx - zone.w / 2 + halfW[i];
      const maxX = zone.cx + zone.w / 2 - halfW[i];
      const minY = zone.cy - zone.h / 2 + halfH[i];
      const maxY = zone.cy + zone.h / 2 - halfH[i];
      pos[i][0] = Math.max(minX, Math.min(maxX, pos[i][0]));
      pos[i][1] = Math.max(minY, Math.min(maxY, pos[i][1]));
    }
  }

  // Check if any overlaps remain after solving
  let hasOverlap = false;
  outer:
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const overlapX = (halfW[i] + halfW[j]) - Math.abs(pos[j][0] - pos[i][0]);
      const overlapY = (halfH[i] + halfH[j]) - Math.abs(pos[j][1] - pos[i][1]);
      if (overlapX > 0.01 && overlapY > 0.01) {
        hasOverlap = true;
        break outer;
      }
    }
  }

  return { pos, hasOverlap };
}

interface SolveResult {
  positions: [number, number, number][];
  cardScale: number;
}

/**
 * Solve positions for cards within a zone, shrinking cards if needed.
 * Tries scale=1.0 first. If overlaps remain after solving, reduces scale
 * and retries until all cards fit without overlap.
 */
function solveZonePositions(
  cards: UnitData[],
  zone: { cx: number; cy: number; w: number; h: number }
): SolveResult {
  const n = cards.length;
  if (n === 0) return { positions: [], cardScale: 1 };
  if (n === 1) {
    return {
      positions: [[zone.cx, zone.cy, 0]],
      cardScale: 1,
    };
  }

  const heats = cards.map(c => c.heat);

  // Try decreasing scales until no overlaps remain
  // Steps: 1.0, 0.9, 0.8, ... 0.3
  let bestScale = 1.0;
  let bestPos: [number, number][] = [];

  for (let s = 1.0; s >= 0.3; s -= 0.05) {
    const { pos, hasOverlap } = runSolver(heats, s, zone);
    bestScale = s;
    bestPos = pos;
    if (!hasOverlap) break;
  }

  return {
    positions: bestPos.map(p => [p[0], p[1], 0] as [number, number, number]),
    cardScale: bestScale,
  };
}

// ============================================================
// Entity data — the core things in the user's world
// ============================================================

interface EntityDef {
  id: string;
  name: string;
  heat: number;
  health?: number;
  value?: string;
  valueLabel?: string;
  color: string;
  isGhost: boolean;
  unitType?: UnitType;
  progress?: number;
  statusTag?: string;
  icon?: string;
  children: ChildPresence[];
}

const ENTITIES: Record<string, EntityDef> = {
  // ---- DEALS (sales-oriented, dollar values) ----
  morrison: {
    id: 'morrison', name: 'Morrison Manufacturing', heat: 0.9, health: 0.85,
    unitType: UnitType.DEAL, value: '$180K', valueLabel: 'Revenue', color: '#E94257', isGhost: false,
    children: [
      { id: 'patricia', name: 'Patricia Simmons', type: PresenceShape.PERSON, color: '#4DC4D2', heat: 0.85, isGhost: false, scale: 0.55, initials: 'PS', label: 'champion', offset: [-1.5, -0.5] },
      { id: 'proposal-doc', name: 'Proposal v3', type: PresenceShape.DOCUMENT, color: '#325BA8', heat: 0.7, isGhost: false, scale: 0.5, label: 'draft', offset: [1.5, -0.4] },
      { id: 'morrison-deal', name: 'Deal — $180K', type: PresenceShape.DEAL, color: '#E94257', heat: 0.9, isGhost: false, scale: 0.6, label: 'negotiation', offset: [0, 1.2] },
    ],
  },
  heartland: {
    id: 'heartland', name: 'Heartland Foods', heat: 0.65, health: 0.78,
    unitType: UnitType.DEAL, value: '$48K', color: '#E94257', isGhost: false,
    children: [
      { id: 'heartland-deal', name: 'Expansion — $48K', type: PresenceShape.DEAL, color: '#E94257', heat: 0.5, isGhost: false, scale: 0.5, label: 'upsell', offset: [0, 0.8] },
      { id: 'tom-bradley', name: 'Tom Bradley', type: PresenceShape.PERSON, color: '#4DC4D2', heat: 0.4, isGhost: false, scale: 0.5, initials: 'TB', offset: [1.2, -0.4] },
    ],
  },
  lakeside: {
    id: 'lakeside', name: 'Lakeside Plastics', heat: 0.85, health: 0.12,
    unitType: UnitType.DEAL, value: '$36K', color: '#E94257', isGhost: false,
    children: [
      { id: 'lakeside-deal', name: 'Deal — Churn Risk', type: PresenceShape.DEAL, color: '#E94257', heat: 0.15, isGhost: false, scale: 0.45, offset: [0, 0.7] },
      { id: 'ghost-champion', name: 'Lost Champion', type: PresenceShape.PERSON, color: '#892987', heat: 0.05, isGhost: true, scale: 0.35, initials: '?', label: 'left company', offset: [-1.0, -0.4] },
    ],
  },

  // ---- PROJECTS (percent complete, progress bars) ----
  enterprise: {
    id: 'enterprise', name: 'Enterprise Tier Launch', heat: 0.75, health: 0.85,
    unitType: UnitType.PROJECT, progress: 0.72, statusTag: 'On Track', color: '#4056A6', isGhost: false,
    children: [
      { id: 'enterprise-proj', name: 'Project Plan', type: PresenceShape.PROJECT, color: '#4056A6', heat: 0.65, isGhost: false, scale: 0.55, offset: [0, 1.0] },
      { id: 'enterprise-doc', name: 'Launch Playbook', type: PresenceShape.DOCUMENT, color: '#325BA8', heat: 0.5, isGhost: false, scale: 0.45, offset: [1.2, -0.5] },
    ],
  },
  'security-questionnaire': {
    id: 'security-questionnaire', name: 'Security Questionnaire', heat: 0.7, health: 0.3,
    unitType: UnitType.PROJECT, progress: 0.30, statusTag: 'Blocked', color: '#E94257', isGhost: false,
    children: [],
  },

  // ---- DOCUMENTS (doc icon, status, last editor) ----
  'morrison-proposal': {
    id: 'morrison-proposal', name: 'Morrison Proposal v3', heat: 0.5, health: 0.7,
    unitType: UnitType.DOCUMENT, statusTag: 'Draft', value: '14 pages', color: '#325BA8', isGhost: false,
    children: [],
  },
  competitive: {
    id: 'competitive', name: 'Competitive Analysis', heat: 0.25, health: 0.45,
    unitType: UnitType.DOCUMENT, statusTag: 'Stale', value: '22 pages', icon: '\u{1F4CA}', color: '#325BA8', isGhost: false,
    children: [
      { id: 'comp-doc', name: 'Analysis Doc', type: PresenceShape.DOCUMENT, color: '#325BA8', heat: 0.2, isGhost: false, scale: 0.4, label: '8mo old', offset: [0, 0.7] },
    ],
  },

  // ---- PEOPLE (avatar, role, relationship) ----
  'ghost-ciso': {
    id: 'ghost-ciso', name: 'Mike Thompson', heat: 0.6, health: 0.05,
    unitType: UnitType.PERSON, statusTag: 'Unengaged', color: '#E94257', isGhost: true,
    children: [],
  },
  'ghost-cfo': {
    id: 'ghost-cfo', name: 'Frank Morrison', heat: 0.55, health: 0.1,
    unitType: UnitType.PERSON, statusTag: 'Silent', color: '#E94257', isGhost: true,
    children: [],
  },

  // ---- INITIATIVES (strategic, OKR-style) ----
  pipeline: {
    id: 'pipeline', name: 'Pipeline Strategy Q1', heat: 0.4, health: 0.6,
    unitType: UnitType.INITIATIVE, progress: 0.45, value: '3 of 7 KRs met', valueLabel: 'OKR', statusTag: 'Behind', color: '#4056A6', isGhost: false,
    children: [
      { id: 'sarah-chen', name: 'Sarah Chen', type: PresenceShape.PERSON, color: '#4DC4D2', heat: 0.5, isGhost: false, scale: 0.5, initials: 'SC', offset: [-1.2, -0.4] },
      { id: 'pipeline-doc', name: 'Strategy Doc', type: PresenceShape.DOCUMENT, color: '#325BA8', heat: 0.4, isGhost: false, scale: 0.5, offset: [0, 0.8] },
    ],
  },

  // ---- GENERIC (no specific type — just a unit with context) ----
  cascade: {
    id: 'cascade', name: 'Cascade Dynamics', heat: 0.5, health: 0.25,
    unitType: UnitType.DEAL, value: '$95K', color: '#E94257', isGhost: false,
    children: [
      { id: 'cascade-deal', name: 'Deal — $95K', type: PresenceShape.DEAL, color: '#E94257', heat: 0.3, isGhost: false, scale: 0.55, offset: [0, 1.0] },
    ],
  },
  appalachian: {
    id: 'appalachian', name: 'Appalachian Steel', heat: 0.4, health: 0.55,
    unitType: UnitType.DEAL, value: '$65K', color: '#E94257', isGhost: false,
    children: [],
  },
};

// ============================================================
// Zone placements — each entry becomes a card in a zone
// Same entity can appear in multiple zones with different facets
// ============================================================

interface ZonePlacement {
  entityId: string;
  zone: CognitiveZone;
  facetLabel: string;
  heatOverride?: number; // optionally adjust heat per-zone appearance
  lastActivity?: string; // e.g. "2m ago"
  recentChange?: boolean; // pulse dot
}

const ZONE_PLACEMENTS: ZonePlacement[] = [
  // ---- FOCUSED EXECUTION: what you're actively working on ----
  { entityId: 'morrison', zone: CognitiveZone.FOCUSED_EXECUTION, facetLabel: 'Active negotiation · Proposal sent', lastActivity: '2m ago', recentChange: true },
  { entityId: 'heartland', zone: CognitiveZone.FOCUSED_EXECUTION, facetLabel: 'Upsell · Expansion proposal sent', lastActivity: '45m ago' },
  { entityId: 'enterprise', zone: CognitiveZone.FOCUSED_EXECUTION, facetLabel: 'Sprint 4 · 72% complete · Kevin on pricing', lastActivity: '3h ago' },

  // ---- REFLECTIVE THINKING: strategy & analysis ----
  { entityId: 'pipeline', zone: CognitiveZone.REFLECTIVE_THINKING, facetLabel: '3 of 7 key results met · Q1 review due', lastActivity: '1w ago' },
  { entityId: 'competitive', zone: CognitiveZone.REFLECTIVE_THINKING, facetLabel: '8 months stale · 2 new competitors since', lastActivity: '8mo ago' },

  // ---- REACTIVE TRIAGE: needs your attention ----
  { entityId: 'lakeside', zone: CognitiveZone.REACTIVE_TRIAGE, facetLabel: 'Churn risk · Champion left · NPS 4', lastActivity: '1d ago', recentChange: true },
  { entityId: 'cascade', zone: CognitiveZone.REACTIVE_TRIAGE, facetLabel: 'Gone dark · 3 weeks no response', lastActivity: '3w ago' },
  { entityId: 'morrison', zone: CognitiveZone.REACTIVE_TRIAGE, facetLabel: '3 gaps · CISO not engaged · CFO silent', heatOverride: 0.85, lastActivity: '15m ago', recentChange: true },
  { entityId: 'ghost-ciso', zone: CognitiveZone.REACTIVE_TRIAGE, facetLabel: 'Not engaged · Required for Stage 2 sign-off', lastActivity: '5d ago' },
  { entityId: 'ghost-cfo', zone: CognitiveZone.REACTIVE_TRIAGE, facetLabel: 'Silent 12 days · Matches historical loss pattern', lastActivity: '12d ago' },

  // ---- COLLABORATIVE / WAITING: in others' hands ----
  { entityId: 'morrison-proposal', zone: CognitiveZone.COLLABORATIVE_WAITING, facetLabel: 'With James for pricing review · 2 days', lastActivity: '2d ago' },
  { entityId: 'security-questionnaire', zone: CognitiveZone.COLLABORATIVE_WAITING, facetLabel: '30% done · Blocked on Greg · 14 days', lastActivity: '14d ago' },
  { entityId: 'appalachian', zone: CognitiveZone.COLLABORATIVE_WAITING, facetLabel: 'Awaiting legal review · 4 days', lastActivity: '4d ago' },
  // Security questionnaire also in triage (overdue)
  { entityId: 'security-questionnaire', zone: CognitiveZone.REACTIVE_TRIAGE, facetLabel: 'Overdue · Greg silent 14 days', heatOverride: 0.75, lastActivity: '14d ago', recentChange: true },
];

// ============================================================
// Build positioned cards from entities + placements
// ============================================================

function buildCards(): UnitData[] {
  // Group placements by zone
  const byZone: Record<CognitiveZone, { placement: ZonePlacement; entity: EntityDef }[]> = {
    [CognitiveZone.FOCUSED_EXECUTION]: [],
    [CognitiveZone.REFLECTIVE_THINKING]: [],
    [CognitiveZone.REACTIVE_TRIAGE]: [],
    [CognitiveZone.COLLABORATIVE_WAITING]: [],
  };

  for (const p of ZONE_PLACEMENTS) {
    const entity = ENTITIES[p.entityId];
    if (entity) {
      byZone[p.zone].push({ placement: p, entity });
    }
  }

  const allCards: UnitData[] = [];

  // For each zone, build UnitData cards and solve positions
  for (const zone of Object.values(CognitiveZone)) {
    const entries = byZone[zone];
    if (entries.length === 0) continue;

    // Sort by heat descending so solver seeds hottest near zone center
    entries.sort((a, b) => {
      const heatA = a.placement.heatOverride ?? a.entity.heat;
      const heatB = b.placement.heatOverride ?? b.entity.heat;
      return heatB - heatA;
    });

    // Build temporary UnitData for the solver
    const tempCards: UnitData[] = entries.map(({ placement, entity }) => {
      const heat = placement.heatOverride ?? entity.heat;
      return {
        id: `${entity.id}__${zone}`,
        name: entity.name,
        position: [0, 0, 0] as [number, number, number],
        heat,
        isGhost: entity.isGhost,
        color: entity.color,
        scale: entity.isGhost ? 0.6 : undefined,
        unitType: entity.unitType,
        health: entity.health,
        value: entity.value,
        valueLabel: entity.valueLabel,
        progress: entity.progress,
        statusTag: entity.statusTag,
        icon: entity.icon,
        zone,
        facetLabel: placement.facetLabel,
        lastActivity: placement.lastActivity,
        recentChange: placement.recentChange,
        children: entity.children,
      };
    });

    // Solve positions and compute cardScale for this zone
    const { positions, cardScale } = solveZonePositions(tempCards, ZONE_REGIONS[zone]);
    for (let i = 0; i < tempCards.length; i++) {
      tempCards[i].position = positions[i];
      tempCards[i].cardScale = cardScale;
    }

    allCards.push(...tempCards);
  }

  return allCards;
}

const INITIAL_UNITS = buildCards();

// ============================================================
// Zone labels component — subtle text in each quadrant
// ============================================================

function ZoneLabels() {
  return (
    <group>
      {Object.values(CognitiveZone).map(zone => {
        const region = ZONE_REGIONS[zone];
        const labels = ZONE_LABELS[zone];
        // Position label at the top of the zone region
        const labelX = region.cx;
        const labelY = region.cy + region.h / 2 - 0.3;

        return (
          <Html
            key={zone}
            center
            position={[labelX, labelY, -0.1]}
            distanceFactor={DISTANCE_FACTOR}
            style={{ pointerEvents: 'none' }}
          >
            <div style={{
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}>
              <div style={{
                fontFamily: '-apple-system, system-ui, sans-serif',
                fontWeight: 600,
                fontSize: 13,
                color: ZONE_COLORS[zone].labelColor,
                letterSpacing: 1,
                textTransform: 'uppercase' as const,
              }}>
                {labels.label}
              </div>
              <div style={{
                fontFamily: '-apple-system, system-ui, sans-serif',
                fontWeight: 400,
                fontSize: 10,
                color: ZONE_COLORS[zone].labelColor.replace('0.35', '0.2'),
                marginTop: 2,
              }}>
                {labels.subtitle}
              </div>
            </div>
          </Html>
        );
      })}
    </group>
  );
}

// ============================================================
// Zone backgrounds — tinted quadrant planes + divider lines
// ============================================================

function ZoneBackgrounds() {
  return (
    <group>
      {/* Tinted background planes for each zone */}
      {Object.values(CognitiveZone).map(zone => {
        const region = ZONE_REGIONS[zone];
        const colors = ZONE_COLORS[zone];
        return (
          <mesh key={zone} position={[region.cx, region.cy, -0.5]}>
            <planeGeometry args={[region.w - 0.08, region.h - 0.08]} />
            <meshBasicMaterial
              color={colors.bg}
              transparent
              opacity={0.03}
              depthWrite={false}
            />
          </mesh>
        );
      })}

      {/* Subtle accent bar at the top of each zone */}
      {Object.values(CognitiveZone).map(zone => {
        const region = ZONE_REGIONS[zone];
        const colors = ZONE_COLORS[zone];
        return (
          <mesh
            key={`${zone}-bar`}
            position={[region.cx, region.cy + region.h / 2 - 0.02, -0.3]}
          >
            <planeGeometry args={[region.w - 0.3, 0.04]} />
            <meshBasicMaterial
              color={colors.accent}
              transparent
              opacity={0.15}
              depthWrite={false}
            />
          </mesh>
        );
      })}

      {/* Divider lines */}
      <mesh position={[0, 0, -0.2]}>
        <planeGeometry args={[14, 0.008]} />
        <meshBasicMaterial color="#000000" transparent opacity={0.05} depthWrite={false} />
      </mesh>
      <mesh position={[0, 0, -0.2]}>
        <planeGeometry args={[0.008, 9]} />
        <meshBasicMaterial color="#000000" transparent opacity={0.05} depthWrite={false} />
      </mesh>
    </group>
  );
}

// ============================================================
// Camera controller
// ============================================================

interface CameraControllerProps {
  expandedUnit: string | null;
  expandedPosition: [number, number, number] | null;
  focusZone: CognitiveZone | null;
  onBackgroundClick: () => void;
}

function CameraController({ expandedUnit, expandedPosition, focusZone, onBackgroundClick }: CameraControllerProps) {
  const { camera, gl } = useThree();
  const isDragging = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });
  const dragDistance = useRef(0);
  const targetPos = useRef({ x: 0, y: 0, z: CAMERA_Z });

  React.useEffect(() => {
    if (expandedUnit && expandedPosition) {
      targetPos.current = {
        x: expandedPosition[0],
        y: expandedPosition[1],
        z: 4.5,
      };
    } else if (focusZone) {
      const region = ZONE_REGIONS[focusZone];
      targetPos.current = {
        x: region.cx,
        y: region.cy,
        z: 3.8, // Close enough to fill viewport with single zone
      };
    } else {
      targetPos.current = { x: 0, y: 0, z: CAMERA_Z };
    }
  }, [expandedUnit, expandedPosition, focusZone]);

  useFrame(() => {
    const dx = targetPos.current.x - camera.position.x;
    const dy = targetPos.current.y - camera.position.y;
    const dz = targetPos.current.z - camera.position.z;
    const lerpSpeed = expandedUnit ? 0.06 : 0.08;
    if (Math.abs(dx) > 0.001) camera.position.x += dx * lerpSpeed;
    if (Math.abs(dy) > 0.001) camera.position.y += dy * lerpSpeed;
    if (Math.abs(dz) > 0.01) camera.position.z += dz * lerpSpeed;
  });

  React.useEffect(() => {
    const el = gl.domElement;

    const onDown = (e: PointerEvent) => {
      isDragging.current = true;
      dragDistance.current = 0;
      lastMouse.current = { x: e.clientX, y: e.clientY };
    };
    const onMove = (e: PointerEvent) => {
      if (!isDragging.current) return;
      const dx = e.clientX - lastMouse.current.x;
      const dy = e.clientY - lastMouse.current.y;
      dragDistance.current += Math.abs(dx) + Math.abs(dy);
      lastMouse.current = { x: e.clientX, y: e.clientY };
      if (!expandedUnit && !focusZone) {
        const panSpeed = camera.position.z * 0.002;
        targetPos.current.x -= dx * panSpeed;
        targetPos.current.y += dy * panSpeed;
      }
    };
    const onUp = (e: PointerEvent) => {
      if (isDragging.current && dragDistance.current < 5) {
        const target = e.target as HTMLElement;
        const isCanvas = target === el;
        const isBackground = target.tagName === 'DIV' && target.style.position === 'fixed';
        if (isCanvas || isBackground) {
          onBackgroundClick();
        }
      }
      isDragging.current = false;
    };

    window.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    el.addEventListener('contextmenu', (e: Event) => e.preventDefault());

    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [gl, camera, expandedUnit, focusZone, onBackgroundClick]);

  return null;
}

// ============================================================
// Main Scene — receives selection handlers from parent
// ============================================================

interface SceneProps {
  units: UnitData[];
  selectedUnit: string | null;
  searchQuery: string;
  focusZone: CognitiveZone | null;
  expandedUnit: string | null;
  expandedPosition: [number, number, number] | null;
  onSelectUnit: (id: string | null) => void;
  onBackgroundClick: () => void;
  onDragStart?: (id: string, e: React.PointerEvent) => void;
}

function Scene({ units, selectedUnit, searchQuery, focusZone, expandedUnit, expandedPosition, onSelectUnit, onBackgroundClick, onDragStart }: SceneProps) {
  const { camera } = useThree();
  const [cameraZ, setCameraZ] = useState(CAMERA_Z);

  useFrame(() => { setCameraZ(camera.position.z); });

  const handleUnitSelect = useCallback((id: string) => {
    onSelectUnit(selectedUnit === id ? null : id);
  }, [selectedUnit, onSelectUnit]);

  // Determine which cards match the search query
  const matchingIds = useMemo(() => {
    if (!searchQuery) return null; // null = no filter, show all
    const q = searchQuery.toLowerCase();
    const ids = new Set<string>();
    for (const unit of units) {
      if (
        unit.name.toLowerCase().includes(q) ||
        (unit.facetLabel && unit.facetLabel.toLowerCase().includes(q)) ||
        (unit.value && unit.value.toLowerCase().includes(q)) ||
        unit.children.some(c => c.name.toLowerCase().includes(q))
      ) {
        ids.add(unit.id);
      }
    }
    return ids;
  }, [searchQuery, units]);

  return (
    <>
      <CameraController
        expandedUnit={expandedUnit}
        expandedPosition={expandedPosition}
        focusZone={focusZone}
        onBackgroundClick={onBackgroundClick}
      />

      <ambientLight intensity={0.9} />

      {/* Zone structure — hidden at unit tier */}
      {!expandedUnit && <ZoneBackgrounds />}
      {!expandedUnit && <ZoneLabels />}

      {/* Cross-zone connection lines — hidden at unit tier */}
      {!expandedUnit && <CrossZoneLinks units={units} selectedUnit={selectedUnit} />}

      {/* Cards — at unit tier only the expanded unit is shown with isExpanded=true */}
      {expandedUnit ? (
        units
          .filter(u => u.id === expandedUnit)
          .map(unit => (
            <UnitPresence
              key={unit.id}
              data={unit}
              cameraZ={cameraZ}
              isExpanded={true}
              selected={false}
              entranceDelay={0}
              onSelect={handleUnitSelect}
            />
          ))
      ) : (
        units.map((unit, i) => (
          <UnitPresence
            key={unit.id}
            data={unit}
            cameraZ={cameraZ}
            isExpanded={false}
            dimmed={matchingIds !== null && !matchingIds.has(unit.id)}
            selected={selectedUnit === unit.id}
            entranceDelay={0.08 * i}
            onSelect={handleUnitSelect}
            onDragStart={onDragStart}
          />
        ))
      )}
    </>
  );
}

// ============================================================
// Cross-zone connection lines
// ============================================================

function computeCrossZonePairs(units: UnitData[]): { a: string; b: string; entityId: string }[] {
  const byEntity: Record<string, string[]> = {};
  for (const u of units) {
    const eid = u.id.split('__')[0];
    if (!byEntity[eid]) byEntity[eid] = [];
    byEntity[eid].push(u.id);
  }
  const pairs: { a: string; b: string; entityId: string }[] = [];
  for (const [eid, ids] of Object.entries(byEntity)) {
    if (ids.length < 2) continue;
    for (let i = 0; i < ids.length; i++) {
      for (let j = i + 1; j < ids.length; j++) {
        pairs.push({ a: ids[i], b: ids[j], entityId: eid });
      }
    }
  }
  return pairs;
}

function CrossZoneLine({ ax, ay, bx, by }: { ax: number; ay: number; bx: number; by: number }) {
  const lineObj = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const positions = new Float32Array([ax, ay, 0.05, bx, by, 0.05]);
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.LineDashedMaterial({
      color: '#999',
      dashSize: 0.15,
      gapSize: 0.1,
      transparent: true,
      opacity: 0.4,
    });
    const line = new THREE.Line(geo, mat);
    line.computeLineDistances();
    return line;
  }, [ax, ay, bx, by]);

  return <primitive object={lineObj} />;
}

function CrossZoneLinks({ units, selectedUnit }: { units: UnitData[]; selectedUnit: string | null }) {
  const selectedEntity = selectedUnit ? selectedUnit.split('__')[0] : null;

  const crossZonePairs = useMemo(() => computeCrossZonePairs(units), [units]);

  // Only show links for the selected entity
  const visiblePairs = useMemo(() => {
    if (!selectedEntity) return [];
    return crossZonePairs.filter(p => p.entityId === selectedEntity);
  }, [selectedEntity, crossZonePairs]);

  if (visiblePairs.length === 0) return null;

  return (
    <group>
      {visiblePairs.map(pair => {
        const unitA = units.find(u => u.id === pair.a);
        const unitB = units.find(u => u.id === pair.b);
        if (!unitA || !unitB) return null;

        return (
          <CrossZoneLine
            key={`${pair.a}-${pair.b}`}
            ax={unitA.position[0]} ay={unitA.position[1]}
            bx={unitB.position[0]} by={unitB.position[1]}
          />
        );
      })}
    </group>
  );
}

// ============================================================
// Helper: get all zone appearances for an entity
// ============================================================

function getZoneAppearances(unitId: string): { zone: CognitiveZone; facetLabel: string }[] {
  const entityId = unitId.split('__')[0];
  return ZONE_PLACEMENTS
    .filter(p => p.entityId === entityId)
    .map(p => ({ zone: p.zone, facetLabel: p.facetLabel }));
}

// ============================================================
// Compose overlay — for Draft Email, Draft Doc, Quick Note, New Chat
// ============================================================

interface ComposeConfig {
  type: 'draft-email' | 'draft-doc' | 'quick-note' | 'new-chat';
  title: string;
  icon: string;
  placeholder: string;
  accent: string;
  submitLabel: string;
}

const COMPOSE_CONFIGS: Record<string, ComposeConfig> = {
  'draft-email': {
    type: 'draft-email',
    title: 'Draft Email',
    icon: '📧',
    placeholder: 'Start writing your email...\n\nAI will suggest recipients, subject, and polish your draft.',
    accent: '#892987',
    submitLabel: 'Generate Draft',
  },
  'draft-doc': {
    type: 'draft-doc',
    title: 'Draft Document',
    icon: '📄',
    placeholder: 'Describe the document you need...\n\nAI will create a structured draft based on your context.',
    accent: '#4056A6',
    submitLabel: 'Generate Draft',
  },
  'quick-note': {
    type: 'quick-note',
    title: 'Quick Note',
    icon: '📝',
    placeholder: 'Capture your thought...\n\nThis note will be linked to your current context.',
    accent: '#059669',
    submitLabel: 'Save Note',
  },
  'new-chat': {
    type: 'new-chat',
    title: 'New Chat',
    icon: '💬',
    placeholder: 'What would you like to discuss?\n\nStart a conversation with your AI teammate.',
    accent: '#325BA8',
    submitLabel: 'Start Chat',
  },
};

function ComposeOverlay({ configKey, onClose, relatedUnit }: {
  configKey: string;
  onClose: () => void;
  relatedUnit?: string | null;
}) {
  const config = COMPOSE_CONFIGS[configKey];
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setTimeout(() => textareaRef.current?.focus(), 100);
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSubmit = () => {
    setSubmitted(true);
    setTimeout(onClose, 1200);
  };

  if (!config) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 2000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0, 0, 0, 0.25)',
      backdropFilter: 'blur(4px)',
      WebkitBackdropFilter: 'blur(4px)',
      fontFamily: '-apple-system, system-ui, sans-serif',
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: 520,
        background: '#FFFFFF',
        borderRadius: 16,
        boxShadow: '0 24px 64px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        animation: 'composeIn 0.2s ease-out',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px 12px',
          borderBottom: '1px solid #E3E5EB',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>{config.icon}</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#111318' }}>
              {config.title}
            </span>
            {relatedUnit && (
              <span style={{
                fontSize: 11,
                fontWeight: 500,
                color: config.accent,
                background: `${config.accent}10`,
                padding: '2px 8px',
                borderRadius: 4,
              }}>
                linked to context
              </span>
            )}
          </div>
          <button onClick={onClose} style={{
            border: 'none',
            background: 'rgba(0,0,0,0.04)',
            borderRadius: 6,
            width: 28,
            height: 28,
            cursor: 'pointer',
            fontSize: 14,
            color: '#7A7F8E',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'inherit',
          }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px' }}>
          {submitted ? (
            <div style={{
              textAlign: 'center',
              padding: '32px 0',
            }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111318' }}>
                {config.type === 'quick-note' ? 'Note saved' :
                 config.type === 'new-chat' ? 'Chat started' :
                 'Draft generated'}
              </div>
              <div style={{ fontSize: 12, color: '#7A7F8E', marginTop: 4 }}>
                {config.type === 'quick-note' ? 'Linked to your current context' :
                 config.type === 'new-chat' ? 'Opening conversation...' :
                 'Opening in editor...'}
              </div>
            </div>
          ) : (
            <>
              <textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={config.placeholder}
                style={{
                  width: '100%',
                  minHeight: 140,
                  border: '1px solid #E3E5EB',
                  borderRadius: 10,
                  padding: 14,
                  fontSize: 14,
                  fontFamily: '-apple-system, system-ui, sans-serif',
                  color: '#111318',
                  resize: 'vertical',
                  outline: 'none',
                  lineHeight: 1.5,
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  (e.target as HTMLTextAreaElement).style.borderColor = config.accent;
                  (e.target as HTMLTextAreaElement).style.boxShadow = `0 0 0 3px ${config.accent}18`;
                }}
                onBlur={(e) => {
                  (e.target as HTMLTextAreaElement).style.borderColor = '#E3E5EB';
                  (e.target as HTMLTextAreaElement).style.boxShadow = 'none';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && value.trim()) {
                    handleSubmit();
                  }
                }}
              />
              {/* AI suggestion hint */}
              {config.type !== 'quick-note' && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 10,
                  fontSize: 11,
                  color: '#7A7F8E',
                }}>
                  <span style={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #2563EB, #7C3AED)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 8,
                    color: '#FFFFFF',
                    fontWeight: 800,
                    flexShrink: 0,
                  }}>AI</span>
                  {config.type === 'draft-email'
                    ? 'AI will suggest recipients and subject from your recent context'
                    : config.type === 'new-chat'
                    ? 'Your AI teammate will have full context of your current work'
                    : 'AI will structure your document based on relevant materials'}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!submitted && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 20px 16px',
            borderTop: '1px solid #E3E5EB',
          }}>
            <span style={{ fontSize: 11, color: '#A3A8B4' }}>
              {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter to submit
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={onClose} style={{
                border: '1px solid #E3E5EB',
                background: '#FFFFFF',
                borderRadius: 8,
                padding: '7px 16px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500,
                color: '#4A4F5C',
                fontFamily: 'inherit',
              }}>
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!value.trim()}
                style={{
                  border: 'none',
                  background: value.trim() ? config.accent : '#D4D7DE',
                  borderRadius: 8,
                  padding: '7px 18px',
                  cursor: value.trim() ? 'pointer' : 'default',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#FFFFFF',
                  fontFamily: 'inherit',
                  transition: 'background 0.15s',
                }}
              >
                {config.submitLabel}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes composeIn {
          from { opacity: 0; transform: scale(0.95) translateY(8px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ============================================================
// Toast notification — brief feedback for quick actions
// ============================================================

function ToastNotification({ message, icon, onDone }: {
  message: string;
  icon: string;
  onDone: () => void;
}) {
  useEffect(() => {
    const timer = setTimeout(onDone, 2000);
    return () => clearTimeout(timer);
  }, [onDone]);

  return (
    <div style={{
      position: 'fixed',
      bottom: 80,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 2000,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      background: '#111318',
      color: '#FFFFFF',
      borderRadius: 10,
      padding: '10px 18px',
      fontSize: 13,
      fontWeight: 500,
      fontFamily: '-apple-system, system-ui, sans-serif',
      boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      animation: 'toastIn 0.25s ease-out',
    }}>
      <span style={{ fontSize: 15 }}>{icon}</span>
      {message}
      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateX(-50%) translateY(12px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ============================================================
// Search overlay
// ============================================================

function SearchOverlay({ units, query, onChange, onClose }: {
  units: UnitData[];
  query: string;
  onChange: (q: string) => void;
  onClose: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Count matches
  const matchCount = useMemo(() => {
    if (!query) return units.length;
    const q = query.toLowerCase();
    return units.filter(u =>
      u.name.toLowerCase().includes(q) ||
      (u.facetLabel && u.facetLabel.toLowerCase().includes(q)) ||
      (u.value && u.value.toLowerCase().includes(q)) ||
      u.children.some(c => c.name.toLowerCase().includes(q))
    ).length;
  }, [query, units]);

  return (
    <div style={{
      position: 'fixed',
      top: 20,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 950,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      background: 'rgba(255, 255, 255, 0.96)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderRadius: 14,
      padding: '10px 16px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)',
      minWidth: 320,
    }}>
      <span style={{ fontSize: 16, opacity: 0.5 }}>🔍</span>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search units, people, values..."
        style={{
          flex: 1,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontFamily: '-apple-system, system-ui, sans-serif',
          fontSize: 15,
          color: '#333',
        }}
      />
      <span style={{
        fontSize: 11,
        color: '#999',
        whiteSpace: 'nowrap',
        fontFamily: '-apple-system, system-ui, sans-serif',
      }}>
        {matchCount} match{matchCount !== 1 ? 'es' : ''}
      </span>
      <button
        onClick={onClose}
        style={{
          border: 'none',
          background: 'rgba(0,0,0,0.04)',
          borderRadius: 6,
          width: 24,
          height: 24,
          cursor: 'pointer',
          fontSize: 12,
          color: '#999',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          fontFamily: 'inherit',
        }}
      >
        ✕
      </button>
    </div>
  );
}

// ============================================================
// Exported page component
// ============================================================

const ZONE_ORDER: CognitiveZone[] = [
  CognitiveZone.FOCUSED_EXECUTION,
  CognitiveZone.REACTIVE_TRIAGE,
  CognitiveZone.REFLECTIVE_THINKING,
  CognitiveZone.COLLABORATIVE_WAITING,
];

// ============================================================
// Drag state types
// ============================================================

interface DragState {
  unitId: string;
  unitName: string;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  sourceZone: CognitiveZone;
}

/**
 * Given screen coordinates relative to the canvas area, determine
 * which zone quadrant they fall in (simple 2x2 grid).
 */
function screenToZone(x: number, y: number, rect: DOMRect): CognitiveZone | null {
  const relX = (x - rect.left) / rect.width;
  const relY = (y - rect.top) / rect.height;
  if (relX < 0 || relX > 1 || relY < 0 || relY > 1) return null;

  const isLeft = relX < 0.5;
  const isTop = relY < 0.5;

  if (isLeft && isTop) return CognitiveZone.FOCUSED_EXECUTION;
  if (!isLeft && isTop) return CognitiveZone.REACTIVE_TRIAGE;
  if (isLeft && !isTop) return CognitiveZone.REFLECTIVE_THINKING;
  return CognitiveZone.COLLABORATIVE_WAITING;
}

export default function FieldPrototype() {
  const [units, setUnits] = useState<UnitData[]>(INITIAL_UNITS);
  const [selectedUnit, setSelectedUnit] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusZone, setFocusZone] = useState<CognitiveZone | null>(null);
  const [composeOpen, setComposeOpen] = useState<string | null>(null); // key into COMPOSE_CONFIGS
  const [toast, setToast] = useState<{ message: string; icon: string } | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [dragOverZone, setDragOverZone] = useState<CognitiveZone | null>(null);
  const [expandedUnit, setExpandedUnit] = useState<string | null>(null);
  const [expandedPosition, setExpandedPosition] = useState<[number, number, number] | null>(null);
  const [showWalkthrough, setShowWalkthrough] = useState(true);
  const canvasAreaRef = useRef<HTMLDivElement>(null);

  // Scroll accumulator for discrete tier zoom
  const scrollAccum = useRef(0);
  const scrollDecayTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tierCooldown = useRef(false);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      // Don't capture scroll when compose overlay or search is active
      if (composeOpen) return;
      // Only intercept if the scroll target is within the canvas area
      if (canvasAreaRef.current && !canvasAreaRef.current.contains(e.target as Node)) return;

      e.preventDefault();

      // Block input during cooldown after a tier transition
      if (tierCooldown.current) return;

      // Accumulate scroll delta
      scrollAccum.current += e.deltaY;

      // Reset decay timer
      if (scrollDecayTimer.current) clearTimeout(scrollDecayTimer.current);
      scrollDecayTimer.current = setTimeout(() => {
        scrollAccum.current = 0;
      }, 400);

      const THRESHOLD = 80;

      // Determine current tier
      const currentTier = expandedUnit ? 'unit' : focusZone ? 'zone' : 'workspace';

      const startCooldown = () => {
        tierCooldown.current = true;
        scrollAccum.current = 0;
        setTimeout(() => { tierCooldown.current = false; }, 600);
      };

      if (scrollAccum.current > THRESHOLD) {
        // Scroll down — zoom in
        if (currentTier === 'workspace') {
          // Zoom into the zone under the cursor
          if (canvasAreaRef.current) {
            const rect = canvasAreaRef.current.getBoundingClientRect();
            const zone = screenToZone(e.clientX, e.clientY, rect);
            if (zone) {
              setFocusZone(zone);
              startCooldown();
            }
          }
        } else if (currentTier === 'zone' && focusZone && canvasAreaRef.current) {
          // Zoom into the card nearest the cursor
          const zoneUnits = units.filter(u => u.zone === focusZone);
          if (zoneUnits.length > 0) {
            const rect = canvasAreaRef.current.getBoundingClientRect();
            // Convert screen coords to NDC
            const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            const ndcY = -((e.clientY - rect.top) / rect.height) * 2 + 1;
            // Approximate world position from NDC using zone-tier camera
            const region = ZONE_REGIONS[focusZone];
            const camZ = 3.8;
            const aspect = rect.width / rect.height;
            const halfH = Math.tan((FOV / 2) * Math.PI / 180) * camZ;
            const halfW = halfH * aspect;
            const worldX = region.cx + ndcX * halfW;
            const worldY = region.cy + ndcY * halfH;
            // Find closest card to cursor world position
            let closest = zoneUnits[0];
            let closestDist = Infinity;
            for (const u of zoneUnits) {
              const dx = u.position[0] - worldX;
              const dy = u.position[1] - worldY;
              const dist = dx * dx + dy * dy;
              if (dist < closestDist) {
                closestDist = dist;
                closest = u;
              }
            }
            setExpandedUnit(closest.id);
            setExpandedPosition(closest.position);
            // If detail panel is open, transition it to the new card
            if (selectedUnit) {
              setSelectedUnit(closest.id);
            }
            startCooldown();
          }
        }
        // At unit tier, scroll down does nothing
      } else if (scrollAccum.current < -THRESHOLD) {
        // Scroll up — zoom out
        if (currentTier === 'unit') {
          setExpandedUnit(null);
          setExpandedPosition(null);
          startCooldown();
        } else if (currentTier === 'zone') {
          setFocusZone(null);
          startCooldown();
        }
        // At workspace tier, scroll up does nothing
      }
    };

    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      document.removeEventListener('wheel', handleWheel);
      if (scrollDecayTimer.current) clearTimeout(scrollDecayTimer.current);
    };
  }, [expandedUnit, focusZone, units, composeOpen]);

  // Background click handler — zooms out one tier
  const handleBackgroundClick = useCallback(() => {
    if (expandedUnit) {
      setExpandedUnit(null);
      setExpandedPosition(null);
    } else if (focusZone) {
      setFocusZone(null);
    } else {
      setSelectedUnit(null);
    }
  }, [expandedUnit, focusZone]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      // Don't capture when compose or search input is focused
      if (composeOpen) {
        if (e.key === 'Escape') setComposeOpen(null);
        return;
      }
      if (searchOpen && e.key !== 'Escape' && !(e.metaKey || e.ctrlKey)) return;

      // Cmd+K — toggle search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(prev => !prev);
        if (searchOpen) setSearchQuery('');
        return;
      }

      // 1-4 — jump to zone
      if (e.key >= '1' && e.key <= '4' && !e.metaKey && !e.ctrlKey) {
        const idx = parseInt(e.key) - 1;
        const zone = ZONE_ORDER[idx];
        setExpandedUnit(null);
        setExpandedPosition(null);
        setFocusZone(prev => prev === zone ? null : zone);
        return;
      }

      // 0 or Escape — back one tier, then overview
      if (e.key === '0' || (e.key === 'Escape' && !searchOpen)) {
        if (expandedUnit) {
          setExpandedUnit(null);
          setExpandedPosition(null);
        } else {
          setFocusZone(null);
          setSelectedUnit(null);
        }
        return;
      }

      // Tab — cycle zones
      if (e.key === 'Tab' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setFocusZone(prev => {
          if (!prev) return ZONE_ORDER[0];
          const idx = ZONE_ORDER.indexOf(prev);
          const next = e.shiftKey ? idx - 1 : idx + 1;
          if (next < 0 || next >= ZONE_ORDER.length) return null;
          return ZONE_ORDER[next];
        });
        return;
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [searchOpen, composeOpen]);

  const handleToolbarAction = React.useCallback((actionId: string) => {
    switch (actionId) {
      case 'search':
        setSearchOpen(true);
        break;
      case 'draft-email':
      case 'draft-doc':
      case 'quick-note':
      case 'new-chat':
        setComposeOpen(actionId);
        break;
      case 'new-unit':
        setToast({ message: 'New unit — coming soon', icon: '🔷' });
        break;
      case 'new-task':
        setToast({ message: 'Task created and linked to context', icon: '✅' });
        break;
      case 'schedule':
        setToast({ message: 'Schedule — coming soon', icon: '📅' });
        break;
      default:
        console.log('[Field] toolbar action:', actionId);
    }
  }, []);

  // ---- Drag between zones ----
  const handleDragStart = useCallback((unitId: string, e: React.PointerEvent) => {
    const unit = units.find(u => u.id === unitId);
    if (!unit) return;
    setDrag({
      unitId,
      unitName: unit.name,
      startX: e.clientX,
      startY: e.clientY,
      currentX: e.clientX,
      currentY: e.clientY,
      sourceZone: unit.zone,
    });
  }, [units]);

  // Pointer move / up at window level during drag
  useEffect(() => {
    if (!drag) return;

    const onMove = (e: PointerEvent) => {
      setDrag(prev => prev ? { ...prev, currentX: e.clientX, currentY: e.clientY } : null);
      // Determine which zone cursor is over
      if (canvasAreaRef.current) {
        const rect = canvasAreaRef.current.getBoundingClientRect();
        setDragOverZone(screenToZone(e.clientX, e.clientY, rect));
      }
    };

    const onUp = (e: PointerEvent) => {
      if (!canvasAreaRef.current) { setDrag(null); setDragOverZone(null); return; }

      const rect = canvasAreaRef.current.getBoundingClientRect();
      const targetZone = screenToZone(e.clientX, e.clientY, rect);

      if (targetZone && targetZone !== drag.sourceZone) {
        // Move the card to the new zone — rebuild positions
        setUnits(prev => {
          // Update the dragged unit's zone
          const updated = prev.map(u =>
            u.id === drag.unitId
              ? { ...u, zone: targetZone, facetLabel: `Moved from ${ZONE_LABELS[drag.sourceZone].label}` }
              : u
          );

          // Re-solve positions for affected zones
          const affectedZones = [drag.sourceZone, targetZone];
          let result = [...updated];

          for (const zone of affectedZones) {
            const zoneCards = result.filter(u => u.zone === zone);
            if (zoneCards.length === 0) continue;
            const { positions, cardScale } = solveZonePositions(zoneCards, ZONE_REGIONS[zone]);
            const zoneCardIds = zoneCards.map(c => c.id);
            result = result.map(u => {
              const idx = zoneCardIds.indexOf(u.id);
              if (idx >= 0) {
                return { ...u, position: positions[idx], cardScale };
              }
              return u;
            });
          }

          return result;
        });

        setToast({
          message: `Moved "${drag.unitName}" to ${ZONE_LABELS[targetZone].label}`,
          icon: '↗️',
        });
      }

      setDrag(null);
      setDragOverZone(null);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [drag]);

  const handleSearchClose = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
  }, []);

  const handleDetailAction = React.useCallback((actionId: string, unitId: string) => {
    console.log('[Field] detail action:', actionId, 'on', unitId);
  }, []);

  const selectedUnitData = useMemo(() => {
    if (!selectedUnit) return null;
    return units.find(u => u.id === selectedUnit) ?? null;
  }, [selectedUnit, units]);

  const zoneAppearances = useMemo(() => {
    if (!selectedUnit) return [];
    return getZoneAppearances(selectedUnit);
  }, [selectedUnit]);

  const openSearch = useCallback(() => setSearchOpen(true), []);

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: '#f5f5f5' }}>
      <PageHeader onSearch={openSearch} />

      {/* Join Waitlist — persistent top-left */}
      <button
        onClick={() => { window.location.assign('/#beta'); }}
        style={{
          position: 'fixed',
          top: 60,
          left: 16,
          zIndex: 950,
          border: 'none',
          background: 'linear-gradient(135deg, #2563EB, #7C3AED)',
          color: '#FFFFFF',
          borderRadius: 10,
          padding: '9px 18px',
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 700,
          fontFamily: '-apple-system, system-ui, sans-serif',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          boxShadow: '0 4px 16px rgba(37, 99, 235, 0.3), 0 0 0 1px rgba(255,255,255,0.1) inset',
          letterSpacing: 0.2,
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
          (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4), 0 0 0 1px rgba(255,255,255,0.1) inset';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
          (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(37, 99, 235, 0.3), 0 0 0 1px rgba(255,255,255,0.1) inset';
        }}
      >
        <span style={{ fontSize: 14 }}>◈</span>
        Join the Beta
      </button>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Main canvas area — shrinks when panel is open */}
        <div ref={canvasAreaRef} style={{
          flex: 1,
          position: 'relative',
          transition: 'flex 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          minWidth: 0,
        }}>
          {/* Zone marker divs for walkthrough spotlight targeting */}
          <div data-zone="focused_execution" style={{ position: 'absolute', top: 0, left: 0, width: '50%', height: '50%', pointerEvents: 'none' }} />
          <div data-zone="reactive_triage" style={{ position: 'absolute', top: 0, right: 0, width: '50%', height: '50%', pointerEvents: 'none' }} />
          <div data-zone="reflective_thinking" style={{ position: 'absolute', bottom: 0, left: 0, width: '50%', height: '50%', pointerEvents: 'none' }} />
          <div data-zone="collaborative_waiting" style={{ position: 'absolute', bottom: 0, right: 0, width: '50%', height: '50%', pointerEvents: 'none' }} />
          {/* Card highlight marker for walkthrough — targets the first "hot" card area */}
          <div data-walkthrough="card-highlight" style={{ position: 'absolute', top: '8%', left: '5%', width: '42%', height: '38%', pointerEvents: 'none' }} />

          {/* Breadcrumb tier navigation */}
          {(focusZone || expandedUnit) && (
            <div style={{
              position: 'absolute',
              top: 12,
              left: 16,
              zIndex: 900,
              display: 'flex',
              alignItems: 'center',
              gap: 0,
              fontFamily: '-apple-system, system-ui, sans-serif',
              fontSize: 12,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.92)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              borderRadius: 8,
              padding: '6px 12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)',
            }}>
              <button
                onClick={() => { setExpandedUnit(null); setExpandedPosition(null); setFocusZone(null); setSelectedUnit(null); }}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#2563EB',
                  padding: '2px 4px',
                  borderRadius: 4,
                  fontFamily: 'inherit',
                }}
              >
                Overview
              </button>
              {focusZone && (
                <>
                  <span style={{ color: '#A3A8B4', margin: '0 4px', fontSize: 10 }}>›</span>
                  <button
                    onClick={() => { setExpandedUnit(null); setExpandedPosition(null); }}
                    style={{
                      border: 'none',
                      background: 'none',
                      cursor: expandedUnit ? 'pointer' : 'default',
                      fontSize: 12,
                      fontWeight: expandedUnit ? 500 : 600,
                      color: expandedUnit ? '#2563EB' : ZONE_COLORS[focusZone].accent,
                      padding: '2px 4px',
                      borderRadius: 4,
                      fontFamily: 'inherit',
                    }}
                  >
                    {ZONE_LABELS[focusZone].label}
                  </button>
                </>
              )}
              {expandedUnit && (() => {
                const unitData = units.find(u => u.id === expandedUnit);
                return unitData ? (
                  <>
                    <span style={{ color: '#A3A8B4', margin: '0 4px', fontSize: 10 }}>›</span>
                    <span style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#111318',
                      padding: '2px 4px',
                    }}>
                      {unitData.name}
                    </span>
                  </>
                ) : null;
              })()}
            </div>
          )}

          <Canvas
            camera={{ fov: FOV, near: 0.1, far: 200, position: [0, 0, CAMERA_Z] }}
            gl={{ antialias: true, alpha: false, powerPreference: 'high-performance' }}
            dpr={[1, 2]}
            style={{ background: '#f5f5f5' }}
          >
            <color attach="background" args={['#f5f5f5']} />
            <Scene
              units={units}
              selectedUnit={selectedUnit}
              searchQuery={searchOpen ? searchQuery : ''}
              focusZone={focusZone}
              expandedUnit={expandedUnit}
              expandedPosition={expandedPosition}
              onSelectUnit={setSelectedUnit}
              onBackgroundClick={handleBackgroundClick}
              onDragStart={handleDragStart}
            />
          </Canvas>

          {searchOpen && (
            <SearchOverlay
              units={units}
              query={searchQuery}
              onChange={setSearchQuery}
              onClose={handleSearchClose}
            />
          )}

          {/* Drag zone highlights */}
          {drag && (
            <div style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 900,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gridTemplateRows: '1fr 1fr',
              gap: 2,
              padding: 4,
            }}>
              {[
                CognitiveZone.FOCUSED_EXECUTION,
                CognitiveZone.REACTIVE_TRIAGE,
                CognitiveZone.REFLECTIVE_THINKING,
                CognitiveZone.COLLABORATIVE_WAITING,
              ].map(zone => {
                const isOver = dragOverZone === zone;
                const isSource = drag.sourceZone === zone;
                return (
                  <div key={zone} style={{
                    borderRadius: 12,
                    border: isOver && !isSource
                      ? `2px dashed ${ZONE_COLORS[zone].accent}`
                      : '2px dashed transparent',
                    background: isOver && !isSource
                      ? `${ZONE_COLORS[zone].bg}15`
                      : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease',
                  }}>
                    {isOver && !isSource && (
                      <span style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: ZONE_COLORS[zone].accent,
                        fontFamily: '-apple-system, system-ui, sans-serif',
                        background: 'rgba(255,255,255,0.9)',
                        padding: '6px 14px',
                        borderRadius: 8,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      }}>
                        Drop in {ZONE_LABELS[zone].label}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Drag ghost */}
          {drag && (
            <div style={{
              position: 'fixed',
              left: drag.currentX + 12,
              top: drag.currentY - 16,
              zIndex: 2000,
              pointerEvents: 'none',
              background: '#FFFFFF',
              borderRadius: 10,
              padding: '8px 14px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
              border: '1px solid rgba(0,0,0,0.08)',
              fontFamily: '-apple-system, system-ui, sans-serif',
              fontSize: 13,
              fontWeight: 600,
              color: '#111318',
              whiteSpace: 'nowrap',
              transform: 'rotate(2deg)',
            }}>
              {drag.unitName}
            </div>
          )}

          <FieldToolbar onAction={handleToolbarAction} />
        </div>

        {/* Detail panel — drawer that pushes canvas */}
        <DetailPanel
          unit={selectedUnitData}
          zoneAppearances={zoneAppearances}
          onClose={() => setSelectedUnit(null)}
          onAction={handleDetailAction}
        />
      </div>

      {/* Compose overlay */}
      {composeOpen && (
        <ComposeOverlay
          configKey={composeOpen}
          onClose={() => setComposeOpen(null)}
          relatedUnit={selectedUnit}
        />
      )}

      {/* Toast notifications */}
      {toast && (
        <ToastNotification
          message={toast.message}
          icon={toast.icon}
          onDone={() => setToast(null)}
        />
      )}

      {/* Interactive walkthrough */}
      {showWalkthrough && (
        <Walkthrough onComplete={() => setShowWalkthrough(false)} />
      )}

      {/* Replay tour button — bottom-left, only shows after walkthrough completes */}
      {!showWalkthrough && (
        <button
          onClick={() => setShowWalkthrough(true)}
          style={{
            position: 'fixed',
            bottom: 20,
            left: 20,
            zIndex: 100,
            border: '1px solid #E3E5EB',
            background: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderRadius: 8,
            padding: '6px 12px',
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 500,
            color: '#7A7F8E',
            fontFamily: '-apple-system, system-ui, sans-serif',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}
        >
          <span style={{ fontSize: 13 }}>◈</span>
          Replay Tour
        </button>
      )}
    </div>
  );
}
